import React, { useState } from 'react';
import { adminProductApi } from '../../../../api/adminProductApi';
import { extractApiArray } from '../../../../utils/apiResponse';
import {
  STOCK_HISTORY_TYPES,
  formatStockHistoryDate,
  fmtPrice,
  renderCommentWithSmartLinks,
  StockMovementDetails,
} from '../stockHistoryUtils';
import MobileProductDialog from './MobileProductDialog';
import MobileProductField from './MobileProductField';
import MobileProductFeedback from './MobileProductFeedback';
import useMobileResource from './useMobileResource';
import useMobileMutation from './useMobileMutation';

function HistoryEntry({ row, productId, onSaved, mutation }) {
  const [editing, setEditing] = useState(false);
  const [comment, setComment] = useState(row.comment || '');
  return (
    <article className='mp-history-entry'>
      <div className='mp-history-heading'>
        <strong>{STOCK_HISTORY_TYPES[row.type] || row.type}</strong>
        <time>{row.createdAtDisplay || formatStockHistoryDate(row.createdAt)}</time>
      </div>
      <div className='mp-history-quantity'>
        <span>
          Було <b>{row.qtyBefore}</b>
        </span>
        <strong className={row.qtyChange > 0 ? 'text-success' : 'text-danger'}>
          {row.qtyChange > 0 ? '+' : ''}
          {row.qtyChange}
        </strong>
        <span>
          Стало <b>{row.qtyAfter}</b>
        </span>
      </div>
      <StockMovementDetails row={row} />
      <details className='mp-history-meta'>
        <summary>Закупівельна ціна та користувач</summary>
        <dl className='mp-history-details'>
          <div>
            <dt>Закуп до</dt>
            <dd>{fmtPrice(row.purchasePriceBefore)}</dd>
          </div>
          <div>
            <dt>Ціна операції</dt>
            <dd>{fmtPrice(row.operationPurchasePrice)}</dd>
          </div>
          <div>
            <dt>Закуп після</dt>
            <dd>{fmtPrice(row.purchasePriceAfter)}</dd>
          </div>
          <div>
            <dt>Користувач</dt>
            <dd>{row.userId ?? '—'}</dd>
          </div>
        </dl>
      </details>
      {editing ? (
        <div>
          <MobileProductField
            label='Причина / коментар'
            multiline
            rows='3'
            value={comment}
            disabled={mutation.busy}
            onChange={setComment}
          />
          <div className='mp-inline-actions'>
            <button
              type='button'
              className='btn btn-dark'
              disabled={mutation.busy}
              onClick={() =>
                mutation.run(
                  () => adminProductApi.patchStockHistoryComment(productId, row.id, comment.trim()),
                  () => {
                    setEditing(false);
                    onSaved();
                  },
                )
              }
            >
              Зберегти коментар
            </button>
            <button
              type='button'
              className='btn btn-outline-secondary'
              disabled={mutation.busy}
              onClick={() => {
                setComment(row.comment || '');
                setEditing(false);
              }}
            >
              Скасувати
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className='mp-history-comment'>{renderCommentWithSmartLinks(row.comment, row.type)}</p>
          <button
            type='button'
            className='btn btn-outline-secondary'
            disabled={mutation.busy}
            onClick={() => setEditing(true)}
          >
            Редагувати коментар
          </button>
        </>
      )}
    </article>
  );
}

export function MobileStockHistoryList({ productId, mutation }) {
  const history = useMobileResource(
    () => adminProductApi.getStockHistory(productId).then(extractApiArray),
    productId,
  );
  return (
    <section aria-label='Історія залишків'>
      <MobileProductFeedback
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
      />
      {!history.loading && !history.error && !history.data.length && (
        <p className='mp-note'>Змін залишків ще немає.</p>
      )}
      {history.data.map(row => (
        <HistoryEntry
          key={row.id}
          row={row}
          productId={productId}
          onSaved={history.reload}
          mutation={mutation}
        />
      ))}
    </section>
  );
}

export default function MobileStockHistory({ product, onClose }) {
  const mutation = useMobileMutation();
  const close = () => {
    if (mutation.canClose()) onClose();
  };
  return (
    <MobileProductDialog
      title='Історія залишків'
      subtitle={`#${product.id} · ${product.name}`}
      busy={mutation.busy}
      onClose={close}
    >
      <MobileProductFeedback error={mutation.error} />
      <MobileStockHistoryList productId={product.id} mutation={mutation} />
    </MobileProductDialog>
  );
}
